import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { FilePicker } from "@capawesome/capacitor-file-picker";
import { OverlayEventDetail } from "@ionic/core/components";
import { IonActionSheet, useIonModal, useIonToast } from "@ionic/react";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import { alertCircleOutline } from "ionicons/icons";
import React, { useContext, useEffect, useState } from "react";
import { useAppState } from "../../../AppListenerProvider";
import { AppContext } from "../../../AppStateProvider";
import { PictureUploadType } from "../../../models/pictureUploadType";
import { User } from "../../../models/user";
import { UserType } from "../../../models/usertype";
import BrandsService from "../../../services/brand.service";
import UserService from "../../../services/user.service";
import FullScreenPicture from "../../ComponentsUI/FullScreenPicture/FullScreenPicture";
import DefaultProfilePicturesModal from "../DefaultProfilePicturesModal/DefaultProfilePicturesModal";
import ProfilePictureModal from "../ProfilePictureModal/ProfilePictureModal";

interface ContainerProps {
    editProfile: boolean;
    src: string;
    pictureUploadType?: string;
    isNative: boolean;
}

const defaultButtonsActionSheet = [
    {
        text: 'View',
        data: {
            action: 'view',
        },
    },
    {
        text: 'Take Photo',
        data: {
            action: 'take-photo',
        },
    },
    {
        text: 'Choose from Library',
        data: {
            action: 'choose-from-library',
        },
    },
    {
        text: 'Select',
        data: {
            action: 'select',
        },
    },
    {
        text: 'Cancel',
        role: 'cancel',
        data: {
            action: 'cancel',
        },
    },
]

const ProfilePicture: React.FC<ContainerProps> = (props: ContainerProps) => {
    const { state, dispatch } = useContext(AppContext);
    const [userType, setUserType] = useState<UserType>(UserType.USER);
    const [profilePicSrc, setProfilePicSrc] = useState(props.src);
    const [profilePictureFullScreen, setProfilePictureFullScreen] = useState(false);
    const { isNative } = useAppState();

    const [viewEditProfilePictureOpen, setViewEditProfilePictureOpen] = useState(false);

    const [preview, setPreview] = useState<any>(undefined);

    const [buttonsActionSheet, setButtonsActionSheet] = useState(defaultButtonsActionSheet);

    const [present] = useIonToast();

    const [defaultProfilePictureModalOpen, setDefaultProfilePictureModalOpen] = useState(false);

    const confirmProfilePictureSubmit = async (selectedFile: any) => {
        try {
            console.log('uploading');
            const data = new FormData();
            data.append('file', selectedFile);
            console.log("selected file", selectedFile);
            // @ts-ignore
            data.append('filename', selectedFile.value);
            console.log("filename", selectedFile.value);
            if (userType == UserType.USER) {
                const updatedUser: User = await UserService.uploadUserProfilePicture(state.user.id, data);
                await dispatch({ type: 'setUser', payload: updatedUser });
                console.log("updated user pro pic", updatedUser.profile.profile_pic);
                setProfilePicSrc(updatedUser.profile.profile_pic);
                setPreview(undefined);
            }

            if (userType == UserType.BRAND && props.pictureUploadType == PictureUploadType.PROFILE) {
                const updatedBrand = await BrandsService.uploadBrandProfilePicture(state.user.id, data);
                await dispatch({ type: 'setUser', payload: updatedBrand });
                setProfilePicSrc(updatedBrand.profile.profile_pic);
                setPreview(undefined);
            }

            if (userType == UserType.BRAND && props.pictureUploadType == PictureUploadType.COVER) {
                const updatedBrand = await BrandsService.uploadBrandCoverPicture(state.user.id, data);
                await dispatch({ type: 'setUser', payload: updatedBrand });
                setProfilePicSrc(updatedBrand.profile.cover_pic);
                setPreview(undefined);
            }
            present({
                message: 'Profile picture updated successfully',
                duration: 2000,
                position: 'top',
                color: "success"
            });
        } catch (error) {
            console.error('Error updating profile picture:', error);
            present({
                message: 'Failed to update profile picture',
                duration: 2000,
                position: 'top',
                color: "danger"
            });
        }
    }

    const handleDefaultProfilePictureSelect = async (url: string) => {
        if (!url) return;

        try {
            if (userType == UserType.USER) {
                const updatedProfile: User = await UserService.updateUserProfilePictureByDefault(state.user.id, url);
                await dispatch({ type: 'setUser', payload: updatedProfile });
                setProfilePicSrc(updatedProfile.profile.profile_pic);
                setPreview(undefined);
            }
            present({
                message: 'Profile picture updated successfully',
                duration: 2000,
                position: 'top',
                color: "success"
            });
        } catch (error) {
            console.error('Error updating profile picture:', error);
            present({
                message: 'Failed to update profile picture',
                duration: 2000,
                position: 'top',
                color: "danger"
            });
        }
    };

    function openProfilePictureModal() {
        presentProfilePicModal({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
            },
        });
    }

    const [presentProfilePicModal, dismissProfilePicModal] = useIonModal(ProfilePictureModal, {
        onDismiss: (data: string, role: string) => dismissProfilePicModal(data, role),
        onProfilePictureSubmit: confirmProfilePictureSubmit,
        src: props.src,
        pictureUploadType: props.pictureUploadType,
        preview: preview,
    });

    const getUserType = async () => {
        const retrievedUserType = await UserService.getUserType(state.user.auth_id);
        setUserType(retrievedUserType);
    }

    useEffect(() => {
        console.log("Waiting for props to be mounted");
        setProfilePicSrc(props.src);
        getUserType();
    }, [props.src, userType])

    useEffect(() => {
        if (props.editProfile && !profilePicSrc && props.isNative) {
            setButtonsActionSheet([
                {
                    text: 'Take Photo',
                    data: {
                        action: 'take-photo',
                    },
                },
                {
                    text: 'Choose from Library',
                    data: {
                        action: 'choose-from-library',
                    },
                },
                {
                    text: 'Select',
                    data: {
                        action: 'select',
                    },
                },
                {
                    text: 'Cancel',
                    role: 'cancel',
                    data: {
                        action: 'cancel',
                    },
                },
            ]);
        } else {
            setButtonsActionSheet(defaultButtonsActionSheet);
        }
    }, [props.editProfile, profilePicSrc]);


    const openSettings = async () => {
        if (isNative) {
            try {
                if (Capacitor.getPlatform() === 'ios') {
                    await NativeSettings.openIOS({
                        option: IOSSettings.App,
                    });
                } else if (Capacitor.getPlatform() === 'android') {
                    await NativeSettings.openAndroid({
                        option: AndroidSettings.ApplicationDetails,
                    });
                }
            } catch (error) {
                console.error('Error opening settings:', error);
            }
        }
    };

    const presentPermissionsErrorToast = async () => {
        await present({
            message: 'Open your settings to allow access to your photos so you can upload a profile picture',
            duration: 5000, // Duration in milliseconds
            position: 'top',
            color: "danger",
            icon: alertCircleOutline,
            buttons: [
                {
                    text: 'Ok',
                    handler: async () => {
                        await openSettings();
                    }
                },
                {
                    text: 'Cancel',
                    role: 'cancel'
                }
            ]
        });
    };

    /*
    Logic:
    User is viewing their profile
    1. If the user is viewing their profile, and they don't have a profile picture, then display icon to upload pro pic
    2. If they have a profile picture, then they can click that and change their pro pic using the modal

    User is viewing another profile
    1. If the user cannot edit a profile picture, display the default profile picture
    2. If the user that they are viewing does not have a profile picture, display the default profile picture


     */

    /*
    For some reason rendering this was causing an error
    <IonButton id="open-modal-upload" fill="solid" color="light" size="large" onClick={() => setIsOpen(true)}>
     */

    const handleActionSheet = async (data: any) => {
        if (data) {
            const action = data.action;
            if (action === 'view') {
                // view profile picture
                setViewEditProfilePictureOpen(false);
                setProfilePictureFullScreen(true);
            } else if (action === 'take-photo') {
                setViewEditProfilePictureOpen(false);
                // take photo with camera
                console.log('taking photo');
                await takePhoto();
            } else if (action === 'choose-from-library') {
                setViewEditProfilePictureOpen(false);
                // choose from photo library
                console.log('choosing from library');
                await chooseFromLibrary();
            } else if (action === 'select') {
                setViewEditProfilePictureOpen(false);
                // open default profile pictures modal
                setDefaultProfilePictureModalOpen(true);
            } else {
                setViewEditProfilePictureOpen(false);
                console.log('cancelling');
            }
        } else {
            setViewEditProfilePictureOpen(false);
            console.log('cancelling');
        }
    }

    const takePhoto = async () => {
        try {
            if (isNative) {
                const permissions = await Camera.checkPermissions();
                if (permissions.camera === 'granted' || permissions.camera === 'limited') {
                    console.log('Camera permissions granted');
                } else {
                    console.log('Camera permissions not granted, requesting...');
                    const reqPermRes = await Camera.requestPermissions({ permissions: ['camera'] });
                    if (reqPermRes.camera !== 'granted' && reqPermRes.camera !== 'limited') {
                        console.log('Camera permissions denied');
                        await presentPermissionsErrorToast();
                        return;
                    }
                }
            }

            const image = await Camera.getPhoto({
                quality: 80,
                allowEditing: true,
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera,
            });

            if (!image.webPath && !image.path) {
                console.error('No image path available');
                return;
            }

            const response = await fetch(image.webPath!);
            setPreview(image.webPath);
            const blob = await response.blob();
            const file = new File([blob], `profile_picture_${Date.now()}.${image.format}`, { type: `image/${image.format}` });
            await confirmProfilePictureSubmit(file);
        } catch (error) {
            console.log("Error taking photo", error);
        }
    }

    // Convert image to JPEG with correct orientation using createImageBitmap
    const convertToJpeg = async (blob: Blob): Promise<Blob> => {
        // createImageBitmap automatically respects EXIF orientation
        const imageBitmap = await createImageBitmap(blob);

        const canvas = document.createElement('canvas');
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not get canvas context');
        }

        ctx.drawImage(imageBitmap, 0, 0);
        imageBitmap.close();

        return new Promise((resolve, reject) => {
            canvas.toBlob(
                (jpegBlob) => {
                    if (jpegBlob) {
                        resolve(jpegBlob);
                    } else {
                        reject(new Error('Could not convert to JPEG'));
                    }
                },
                'image/jpeg',
                0.85
            );
        });
    };

    const chooseFromLibrary = async () => {
        try {
            const result = await FilePicker.pickImages({
                limit: 1,
                readData: true,
            });

            if (!result.files || result.files.length === 0) {
                console.log('No file selected');
                return;
            }

            const pickedFile = result.files[0];

            if (!pickedFile.data) {
                console.error('No file data available');
                present({
                    message: 'Could not read the selected image. Please try again.',
                    duration: 3000,
                    position: 'top',
                    color: "danger"
                });
                return;
            }

            // Convert base64 to bytes
            const binaryString = atob(pickedFile.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            // Create blob from bytes
            let blob = new Blob([bytes], { type: pickedFile.mimeType || 'image/jpeg' });

            // Convert to JPEG (createImageBitmap handles orientation automatically)
            try {
                blob = await convertToJpeg(blob);
            } catch (conversionError: any) {
                console.error('Error converting image:', conversionError?.message || conversionError);
                present({
                    message: 'Could not process this image. Please try a different photo.',
                    duration: 3000,
                    position: 'top',
                    color: "danger"
                });
                return;
            }

            const fileName = `profile_picture_${Date.now()}.jpg`;
            const file = new File([blob], fileName, { type: 'image/jpeg' });

            const previewUrl = URL.createObjectURL(blob);
            setPreview(previewUrl);

            await confirmProfilePictureSubmit(file);
        } catch (error: any) {
            console.log("Error choosing from library:", error?.message || error);
        }
    }

    const selectFile = async () => {
        try {
            if (isNative) {
                const permissions = await Camera.checkPermissions();
                if (permissions.photos === 'granted' || permissions.photos === 'limited' || permissions.camera === 'granted' || permissions.camera === 'limited') {
                    console.log('Permissions granted');
                } else {
                    console.log('Permissions denied');
                    const reqPermRes = await Camera.requestPermissions();
                    if (reqPermRes.photos === 'granted' || reqPermRes.photos === 'limited' || reqPermRes.camera === 'granted' || reqPermRes.camera === 'limited') {
                        console.log('Permissions granted');
                    } else {
                        console.log('Permissions denied');
                        // present IonToast to open settings
                        await presentPermissionsErrorToast();
                        return;
                    }
                }
            }

            const image = await Camera.getPhoto({
                quality: 80,
                allowEditing: true,
                resultType: CameraResultType.Uri,
                webUseInput: !isNative,
                source: !isNative ? CameraSource.Photos : CameraSource.Prompt,
            });

            if (!image.webPath && !image.path) {
                console.error('No image path available');
                return;
            }

            let file: File;

            if (isNative) {
                // For native platforms, fetch the file and create a File object
                const response = await fetch(image.webPath!);
                setPreview(image.webPath);
                const blob = await response.blob();
                file = new File([blob], `profile_picture_${Date.now()}.${image.format}`, { type: `image/${image.format}` });
                await confirmProfilePictureSubmit(file);
            } else {
                // For web, set preview and open modal
                if (image.webPath) {
                    setPreview(image.webPath);
                    openProfilePictureModal();
                } else {
                    console.error('No web path available');
                }
            }
        } catch (error) {
            console.log("Error picking image", error);
        }
    }

    const handleProfilePictureClick = () => {
        if (props.editProfile) {
            setViewEditProfilePictureOpen(true);
        } else {
            setProfilePictureFullScreen(true);
        }
    };

    return (
        <>
            <img
                alt="profile"
                className="shrink w-24 h-24 rounded-full cursor-pointer"
                src={profilePicSrc || '/assets/photos/defaultProPic.png'}
                onClick={handleProfilePictureClick}
            />
            {profilePictureFullScreen && (
                <FullScreenPicture
                    src={profilePicSrc || '/assets/photos/defaultProPic.png'}
                    onClose={() => setProfilePictureFullScreen(false)}
                />
            )}
            <IonActionSheet
                isOpen={viewEditProfilePictureOpen}
                buttons={buttonsActionSheet}
                onDidDismiss={(e) => handleActionSheet(e.detail.data)}
            />
            <DefaultProfilePicturesModal
                isOpen={defaultProfilePictureModalOpen}
                onClose={() => setDefaultProfilePictureModalOpen(false)}
                onSelect={handleDefaultProfilePictureSelect}
                currentProfilePicture={profilePicSrc}
            />
        </>
    );
};

export default ProfilePicture;
